import { useCallback, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';

import { PermissionKey } from 'auth/types';
import { GET_TASKS, SET_TASK_COMPLETED } from 'schemas';
import { TTask } from 'types';

export type TMode = 'ACTIVE' | 'FINISHED';

interface IReturnValue {
  tasks: Array<TTask>;
  mode: TMode;
  setMode: (mode: TMode) => void;
  setIsTaskCompleted: (id: number, value: boolean) => void;
  loading: boolean;
}

export const useTasks = (
  permissions: Set<PermissionKey> | null
): IReturnValue => {
  const [mode, setMode] = useState<TMode>('ACTIVE');

  const { data: loadedTasks, loading } = useQuery(GET_TASKS, {
    fetchPolicy: 'no-cache',
    skip: !permissions?.has('worker_task_browse'),
    variables: {
      first: 100,
    },
    onError: console.error,
  });

  const [setTaskCompletedMutation] = useMutation(SET_TASK_COMPLETED, {
    refetchQueries: permissions?.has('worker_task_browse') ? [GET_TASKS] : [],
    awaitRefetchQueries: true,
    onError: console.error,
  });

  const setIsTaskCompleted = useCallback(
    (id, value) => {
      if (!permissions?.has('worker_task_complete')) return;
      return setTaskCompletedMutation({ variables: { id, value } });
    },
    [setTaskCompletedMutation]
  );

  const tasks = loadedTasks
    ? loadedTasks?.tasks?.data?.filter((task: TTask) => {
        switch (mode) {
          case 'ACTIVE':
            return !task.finished_at;
          case 'FINISHED':
            return task.finished_at;
          default:
            return true;
        }
      })
    : [];

  return {
    tasks,
    mode,
    setMode,
    setIsTaskCompleted,
    loading,
  };
};
